import { ChangeDetectionStrategy, Component, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup, UntypedFormControl, Validators } from '@angular/forms';
import {
  MatLegacyDialogRef as MatDialogRef,
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
} from '@angular/material/legacy-dialog';

import { Subscription } from 'rxjs';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { ImportsService } from './../../imports.service';
import { ObjectsManagerService } from 'app/modules/settings/objects-manager/services/objects-manager.service';
import { InputImportDialogData } from '../../imports.types';
import { CustomObject } from 'app/shared/modules/custom-objects/custom-objects.types';
import { generateDownloadableFile } from 'app/shared/functions';
import { FileFormat } from 'app/shared/enums';

@Component({
  selector: 'fields-template-dialog',
  templateUrl: './fields-template-dialog.component.html',
  styleUrls: ['./fields-template-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FieldsTemplateDialogComponent extends OnDestroyMixin implements OnInit {
  private importableObjects: CustomObject[];

  importEnableObjectsCtrl: UntypedFormControl = new UntypedFormControl();
  filteredImportableObjects: CustomObject[];
  importableFieldsForm: FormGroup;
  isLoading: boolean;

  constructor(
    @Inject(MAT_DIALOG_DATA) public dialogData: InputImportDialogData,
    private readonly matDialogRef: MatDialogRef<FieldsTemplateDialogComponent>,
    private readonly formBuilder: FormBuilder,
    private readonly importsService: ImportsService,
    private readonly objectsManagerService: ObjectsManagerService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.getObjects$();
    this.buildForm();
    this.watchObjectsOptionsSearchChanges();
  }

  private buildForm(): void {
    this.importableFieldsForm = this.formBuilder.group({
      object_type: [null, [Validators.required]],
    });
  }

  /**
   * Fetch custom objects, filter objects if import value equals to true.
   * @returns an array of custom objects, assign to a variable to be iterated inside a template.
   */
  private getObjects$(): Subscription {
    return this.objectsManagerService
      .get(this.dialogData.accountId)
      .pipe(untilComponentDestroyed(this))
      .subscribe((objects) => {
        this.importableObjects = objects.filter((object) => object.import === true);
        this.filteredImportableObjects = this.importableObjects;
      });
  }

  private watchObjectsOptionsSearchChanges(): void {
    this.importEnableObjectsCtrl.valueChanges
      .pipe(untilComponentDestroyed(this))
      .subscribe(() => this.filterObjectsOptions());
  }

  private filterObjectsOptions(): void {
    if (!this.importableObjects) return;

    const filteredOptions = this.importEnableObjectsCtrl.value?.toLowerCase();
    this.filteredImportableObjects = this.importableObjects.filter(
      (option) => option.label.toLowerCase().indexOf(filteredOptions) > -1,
    );
  }

  public exportFields(): void {
    const objectType = this.importableFieldsForm.get('object_type').value;
    this.isLoading = true;
    this.importsService.getExportableFieldsFile$(this.dialogData.accountId, objectType).subscribe({
      next: (data) => generateDownloadableFile(data, FileFormat.CSV, `${objectType}_fields-template`.toLowerCase()),
      complete: () => {
        this.isLoading = false;
        this.dismiss({ finished: true });
      },
    });
  }

  public dismiss(data?: { finished: boolean }): void {
    return this.matDialogRef.close(data);
  }
}
